import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, runTransaction, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db, Paths, withBackoff } from '../repositories/firestoreUtils';
import { v4 as uuidv4 } from 'uuid';
import { logSecurity } from '../lib/logging/secureLogger';

export type MessageType = 'notification' | 'alert' | 'info' | 'warning' | 'error' | 'success' | 'system';
export type ChannelPreference = 'in_app' | 'push_notification' | 'email' | 'sms' | 'all_channels' | 'user_preference';
export type PriorityLevel = 'urgent' | 'high' | 'normal' | 'low';

export type ActionButton = {
  button_text: string;
  action_type: string;
  action_value: string;
  button_style: string;
};

export type ChannelName = 'in_app' | 'push_notification' | 'email' | 'sms';
export type ChannelStatusValue = 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED' | 'READ';

export type DeliveryStatus = Partial<Record<ChannelName, ChannelStatusValue>>;

export interface SendMessageInput {
  user_id: string;
  message_content: string; // markdown
  message_type: MessageType;
  channel_preference: ChannelPreference;
  expiration_time: string; // ISO 8601 or duration like 24h, 7d
  action_buttons: ActionButton[];
  priority_level: PriorityLevel;
}

export interface SendMessageOutput {
  message_id: string;
  user_id: string;
  delivery_status: DeliveryStatus;
  timestamp_sent: string; // ISO 8601
  operation_status: 'SUCCESS' | 'ERROR';
}

export class InvalidInputError extends Error { code = 'INVALID_INPUT'; }
export class PermissionError extends Error { code = 'PERMISSION_DENIED'; }
export class NotFoundError extends Error { code = 'NOT_FOUND'; }

type SanitizedContent = { markdown: string; html: string; mentions: string[] };

type Classification = {
  style: 'info' | 'warning' | 'error' | 'success' | 'system' | 'notification' | 'alert';
  icon: string;
  requireAcknowledgement: boolean;
};

type PriorityMapping = { level: number; sound: 'none' | 'soft' | 'normal' | 'alert'; badge: 'none' | 'dot' | 'count' | 'flash' };

export class PlatformMessageService {
  private static instance: PlatformMessageService;

  static getInstance(): PlatformMessageService {
    if (!PlatformMessageService.instance) PlatformMessageService.instance = new PlatformMessageService();
    return PlatformMessageService.instance;
  }

  async sendIncomingPlatformMessage(message_text: string, options?: { baseUrl?: string; platform_message_id?: string; origin?: string; session_id?: string; metadata?: Record<string, any> }): Promise<{ operation_status: string; [k: string]: any }> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      const err = new PermissionError('No authenticated user');
      (err as any).code = 'AUTH_REQUIRED';
      throw err;
    }

    const uid = String(currentUser.uid || '').trim();
    if (!uid) {
      const err = new PermissionError('User ID is empty');
      (err as any).code = 'AUTH_REQUIRED';
      throw err;
    }

    // Get the Firebase ID token for authentication
    let idToken: string | null = null;
    try {
      idToken = await currentUser.getIdToken(false);
    } catch (tokenError: any) {
      console.warn('Failed to get ID token, proceeding without Authorization header:', tokenError);
    }

    const payload = {
      platform_message_id: options?.platform_message_id || uuidv4(),
      user_id: uid,
      message_text,
      origin: options?.origin || 'web',
      session_id: options?.session_id,
      timestamp_iso: new Date().toISOString(),
      metadata: options?.metadata || {}
    };

    const apiBase = (() => {
      if (typeof options?.baseUrl === 'string' && options.baseUrl.trim()) {
        return options.baseUrl.trim().replace(/\/$/, '');
      }
      if (typeof process.env.REACT_APP_API_BASEURL === 'string' && process.env.REACT_APP_API_BASEURL.trim()) {
        return process.env.REACT_APP_API_BASEURL.trim().replace(/\/$/, '');
      }
      if (typeof window !== 'undefined' && window.location) {
        const { protocol, hostname, port } = window.location;
        if (port === '' || port === undefined || port === null) {
          return `${protocol}//${hostname}`;
        }
        if (port === '3000') {
          return `${protocol}//${hostname}:3001`;
        }
        return `${protocol}//${hostname}:${port}`;
      }
      if (typeof globalThis !== 'undefined' && (globalThis as any)?.ORIGIN) {
        return String((globalThis as any).ORIGIN).replace(/\/$/, '');
      }
      return 'http://127.0.0.1:3001';
    })();
    const endpoint = (() => {
      const prodHostnames = new Set([
        'www.projectolivia.tech',
        'projectolivia.tech',
        'www.prest0.ai',
        'prest0.ai',
        'projectolivia.web.app',
        'projectolivia.firebaseapp.com'
      ]);
      try {
        if (typeof window !== 'undefined' && prodHostnames.has(String(window.location.hostname))) {
          return 'https://incomingplatformmessage-3fdbiliy3a-uc.a.run.app';
        }
        if (apiBase.includes('incomingplatformmessage-') && apiBase.includes('run.app')) {
          return apiBase;
        }
      } catch {}
      return `${apiBase}/api/triggers/incoming_platform_message`;
    })();

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || String(data?.operation_status || '').toUpperCase() !== 'SUCCESS') {
        const code = String(data?.code || data?.operation_status || `HTTP_${res.status}` || 'TRIGGER_FAILED').toUpperCase();
        const message = data?.message || `Trigger failed: ${code}`;
        const error = new Error(message);
        (error as any).code = code;
        (error as any).details = data;
        throw error;
      }
      return data;
    } catch (err: any) {
      const code = String(err?.code || err?.status || 'TRIGGER_ERROR').toUpperCase();
      const error = new Error(String(err?.message || 'Failed to process platform message via REST trigger'));
      (error as any).code = code;
      (error as any).details = err?.details || err;
      throw error;
    }
  }

  async sendMessage(input: SendMessageInput): Promise<SendMessageOutput> {
    const nowIso = new Date().toISOString();
    try {
      // Validate locally and check permissions, but DO NOT write directly.
      this.validateInputShape(input);
      const targetUid = String(input.user_id || '').trim();
      const currentUid = getAuth().currentUser?.uid || null;
      if (!currentUid) {
        logSecurity('messaging.auth_failed', { reason: 'no_auth' });
        throw new PermissionError('No authenticated user');
      }
      await this.assertUserExists(targetUid);
      await this.assertPermission(currentUid, targetUid);

      // Route through MCP tool: send_platform_message_to_user
      const { resolveMcpBaseUrl } = await import('../mcp/client/baseUrl');
      const { MCPClient } = await import('../mcp/client/MCPClient');
      const baseUrl = resolveMcpBaseUrl(process.env.REACT_APP_MCP_HTTP_BASEURL);
      const idToken = localStorage.getItem('firebase_id_token') || undefined;
      const client = new MCPClient({ baseUrl, idToken, uid: currentUid });
      await client.initialize();
      const result = await client.executeTool('send_platform_message_to_user', {
        user_id: input.user_id,
        message_content: input.message_content,
        message_type: input.message_type,
        channel_preference: input.channel_preference,
        expiration_time: input.expiration_time,
        action_buttons: input.action_buttons,
        priority_level: input.priority_level,
      });

      return {
        message_id: String((result as any)?.message_id || ''),
        user_id: String((result as any)?.user_id || input.user_id),
        delivery_status: (result as any)?.delivery_status || { in_app: 'SENT' },
        timestamp_sent: String((result as any)?.timestamp_sent || nowIso),
        operation_status: String((result as any)?.operation_status || 'SUCCESS') as any,
      };
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('PlatformMessageService.sendMessage failed:', e?.message || e);
      try { logSecurity('messaging.error', { error: String(e?.message || e) }); } catch {}
      return {
        message_id: '',
        user_id: String(input?.user_id || ''),
        delivery_status: {},
        timestamp_sent: nowIso,
        operation_status: 'ERROR',
      };
    }
  }

  private async maybeSendAutoReplyFallback(uid: string): Promise<Partial<SendMessageOutput> | null> {
    try {
      // Read admin_config/general to detect test/auto‑reply instruction
      const ref = doc(db, 'admin_config', 'general');
      const snap = await withBackoff(() => getDoc(ref));
      const data: any = snap.exists() ? (snap.data() as any) : {};
      const nature: string = String(data?.general_nature || '').toLowerCase();
      const behaviors: string = String(data?.general_behaviors || '').toLowerCase();
      const mentionsPlatform = behaviors.includes('platform message');
      const mentionsReply = behaviors.includes('message back');
      const mentionsSuccess = behaviors.includes('test') && (behaviors.includes('success') || behaviors.includes('successful'));
      const isTestMode = nature.includes('test mode');
      if (!isTestMode && !(mentionsPlatform && mentionsReply && mentionsSuccess)) return null;

      const id = await this.sendInAppMessageDirect(uid, {
        message_content: '✅ test successful — we received your platform message.',
        message_type: 'success',
        expiration_time: '24h',
        action_buttons: [
          { button_text: 'dismiss', action_type: 'dismiss', action_value: 'ok', button_style: 'secondary' }
        ],
        priority_level: 'normal'
      });
      return {
        message_id: id,
        user_id: uid,
        delivery_status: { in_app: 'SENT' },
        timestamp_sent: new Date().toISOString(),
        operation_status: 'SUCCESS',
      };
    } catch {
      return null;
    }
  }

  private async sendInAppMessageDirect(
    uid: string,
    input: {
      message_content: string;
      message_type: MessageType;
      expiration_time: string;
      action_buttons: ActionButton[];
      priority_level: PriorityLevel;
    }
  ): Promise<string> {
    const nowIso = new Date().toISOString();
    const messageId = uuidv4();
    const exp = this.parseExpiration(String(input?.expiration_time || '2h'));
    const content = this.sanitizeAndRenderContent(String(input?.message_content || ''));
    const payload: any = {
      user_id: uid,
      message_id: messageId,
      message_type: String(input?.message_type || 'info'),
      channel_preference: 'in_app',
      priority_level: String(input?.priority_level || 'normal'),
      source_tool: 'send_platform_message_to_user',
      content: { markdown: content.markdown, html: content.html },
      action_buttons: this.sanitizeButtons(Array.isArray(input?.action_buttons) ? input!.action_buttons : []),
      created_at: serverTimestamp(),
      created_at_iso: nowIso,
      expires_at_iso: exp ? exp.toISOString() : null,
      expires_at: exp ? Timestamp.fromDate(exp) : null,
      read: false,
      in_app: { status: 'SENT', delivered_at: serverTimestamp(), delivered_at_iso: nowIso }
    };
    const ref = Paths.userPlatformMessageDoc(uid, messageId);
    await withBackoff(() => setDoc(ref, payload, { merge: false } as any));
    return messageId;
  }

  private validateInputShape(input: SendMessageInput): void {
    const required: Array<keyof SendMessageInput> = [
      'user_id',
      'message_content',
      'message_type',
      'channel_preference',
      'expiration_time',
      'action_buttons',
      'priority_level',
    ];
    for (const k of required) {
      if ((input as any)[k] === undefined || (input as any)[k] === null) {
        throw new InvalidInputError(`Missing required field: ${String(k)}`);
      }
    }

    // Message type and priority enums are enforced by TypeScript; add runtime checks for safety
    const typeSet: Set<string> = new Set(['notification','alert','info','warning','error','success','system']);
    if (!typeSet.has(String(input.message_type))) throw new InvalidInputError('Unsupported message_type');
    const prefSet: Set<string> = new Set(['in_app','push_notification','email','sms','all_channels','user_preference']);
    if (!prefSet.has(String(input.channel_preference))) throw new InvalidInputError('Unsupported channel_preference');
    const priSet: Set<string> = new Set(['urgent','high','normal','low']);
    if (!priSet.has(String(input.priority_level))) throw new InvalidInputError('Unsupported priority_level');

    const content = String(input.message_content || '');
    if (!content.trim()) throw new InvalidInputError('message_content is empty');
    if (content.length > 10000) throw new InvalidInputError('message_content exceeds 10,000 characters');

    if (!Array.isArray(input.action_buttons)) throw new InvalidInputError('action_buttons must be an array');
    for (const b of input.action_buttons) {
      if (!b || typeof b !== 'object') throw new InvalidInputError('action_buttons contains invalid entry');
      if (!b.button_text || !b.action_type || !b.action_value || !b.button_style) throw new InvalidInputError('action_buttons item missing fields');
    }

    // expiration_time basic validation
    const exp = String(input.expiration_time || '').trim();
    if (!exp) throw new InvalidInputError('expiration_time must be provided');
    const expParsed = this.parseExpiration(exp);
    if (!expParsed) throw new InvalidInputError('expiration_time must be ISO 8601 or duration like 24h, 7d');
  }

  private async assertUserExists(uid: string): Promise<void> {
    const topRef = Paths.userDoc(uid);
    const [top, profile, settings] = await Promise.all([
      withBackoff(() => getDoc(topRef)).catch(() => null),
      withBackoff(() => getDoc(Paths.userProfileSettingsDoc(uid))).catch(() => null),
      withBackoff(() => getDoc(Paths.userSettingsDoc(uid))).catch(() => null),
    ]);
    const exists = Boolean((top && top.exists()) || (profile && profile.exists()) || (settings && settings.exists()));
    if (!exists) throw new NotFoundError(`Target user not found: ${uid}`);
  }

  private async assertPermission(currentUid: string, targetUid: string): Promise<void> {
    if (currentUid === targetUid) return;
    // Non-self sending requires admin_access in user_settings
    try {
      const snap = await withBackoff(() => getDoc(Paths.userSettingsDoc(currentUid)));
      const data: any = snap.exists() ? (snap.data() as any) : {};
      const adminAccess = Boolean(data?.admin_access === true || ((Array.isArray(data?.profile_settings) ? data.profile_settings[0]?.admin_access : undefined) === true));
      if (!adminAccess) throw new PermissionError('Sender lacks permission to message other users');
      logSecurity('messaging.permission_ok', { currentUid, targetUid, adminAccess: true });
    } catch (e) {
      if (e instanceof PermissionError) throw e;
      logSecurity('messaging.permission_fail', { currentUid, targetUid });
      throw new PermissionError('Permission check failed');
    }
  }

  private async enforceRateLimit(_uid: string): Promise<void> { /* no-op */ }

  private async enforceDoNotDisturb(_targetUid: string, _input: SendMessageInput): Promise<void> { /* no-op */ }

  private sanitizeAndRenderContent(markdown: string): SanitizedContent {
    const escaped = this.escapeHtml(markdown);
    // Mentions: @username or @uid:XXXXXXXX — capture for metadata and stylize
    const mentions = Array.from(new Set<string>((escaped.match(/@([a-zA-Z0-9_:\-]{2,64})/g) || []).map((m) => m.slice(1))));

    // Very small, safe markdown subset → sanitized HTML
    let html = escaped;
    // code blocks ```
    html = html.replace(/```([\s\S]*?)```/g, (_m, p1) => `<pre><code>${this.escapeHtml(p1)}</code></pre>`);
    // inline code `code`
    html = html.replace(/`([^`]+)`/g, (_m, p1) => `<code>${this.escapeHtml(p1)}</code>`);
    // bold **text**
    html = html.replace(/\*\*([^*]+)\*\*/g, (_m, p1) => `<strong>${p1}</strong>`);
    // italic *text*
    html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, (_m, p1) => `<em>${p1}</em>`);
    // links [text](url) — allow only http/https/mailto
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
      const cleanUrl = String(url || '').trim();
      if (!/^https?:\/\//i.test(cleanUrl) && !/^mailto:/i.test(cleanUrl)) return `${text}`;
      const safeText = this.escapeHtml(text);
      const safeUrl = this.escapeAttribute(cleanUrl);
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
    });
    // mentions styling
    html = html.replace(/@([a-zA-Z0-9_:\-]{2,64})/g, (_m, name) => `<span class="mention" data-mention="${this.escapeAttribute(name)}">@${this.escapeHtml(name)}</span>`);

    return { markdown, html, mentions };
  }

  private escapeHtml(input: string): string {
    return String(input || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private escapeAttribute(input: string): string {
    return this.escapeHtml(input).replace(/`/g, '&#96;');
  }

  private parseExpiration(value: string): Date | null {
    const v = String(value || '').trim();
    if (!v) return null;
    // ISO 8601
    const iso = Date.parse(v);
    if (!Number.isNaN(iso)) return new Date(iso);
    // duration like 24h, 7d
    const m = v.match(/^(\d+)([smhdw])$/i);
    if (!m) return null;
    const amount = parseInt(m[1], 10);
    const unit = m[2].toLowerCase();
    let ms = 0;
    switch (unit) {
      case 's': ms = amount * 1000; break;
      case 'm': ms = amount * 60 * 1000; break;
      case 'h': ms = amount * 60 * 60 * 1000; break;
      case 'd': ms = amount * 24 * 60 * 60 * 1000; break;
      case 'w': ms = amount * 7 * 24 * 60 * 60 * 1000; break;
      default: return null;
    }
    return new Date(Date.now() + ms);
  }

  private classifyType(t: MessageType): Classification {
    const base: Record<MessageType, Classification> = {
      notification: { style: 'notification', icon: 'bell', requireAcknowledgement: false },
      alert: { style: 'alert', icon: 'alert-triangle', requireAcknowledgement: true },
      info: { style: 'info', icon: 'info', requireAcknowledgement: false },
      warning: { style: 'warning', icon: 'alert-circle', requireAcknowledgement: false },
      error: { style: 'error', icon: 'x-circle', requireAcknowledgement: true },
      success: { style: 'success', icon: 'check-circle', requireAcknowledgement: false },
      system: { style: 'system', icon: 'settings', requireAcknowledgement: false },
    };
    return base[t];
  }

  private mapPriority(p: PriorityLevel): PriorityMapping {
    const map: Record<PriorityLevel, PriorityMapping> = {
      urgent: { level: 100, sound: 'alert', badge: 'flash' },
      high: { level: 75, sound: 'normal', badge: 'count' },
      normal: { level: 50, sound: 'soft', badge: 'dot' },
      low: { level: 25, sound: 'none', badge: 'none' },
    };
    return map[p];
  }

  private async resolveChannels(_pref: ChannelPreference, _targetUid: string): Promise<ChannelName[]> {
    return ['in_app'];
  }

  private sanitizeButtons(btns: ActionButton[]): ActionButton[] {
    return (Array.isArray(btns) ? btns : []).map((b) => ({
      button_text: String(b.button_text || '').slice(0, 64),
      action_type: String(b.action_type || '').slice(0, 64),
      action_value: String(b.action_value || '').slice(0, 2048),
      button_style: String(b.button_style || '').slice(0, 32),
    }));
  }

  private async createMessageDocument(targetUid: string, payload: Record<string, any>): Promise<string> {
    // Concurrency-safe UUID assignment: attempt with transaction; if collision, retry
    const maxAttempts = 3;
    for (let i = 0; i < maxAttempts; i += 1) {
      const candidate = uuidv4();
      const ref = Paths.userNotificationDoc(targetUid, candidate);
      try {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          if (snap.exists()) throw new Error('ID_COLLISION');
          tx.set(ref, { message_id: candidate, ...payload }, { merge: false });
        });
        return candidate;
      } catch (e: any) {
        const code = String(e?.code || e?.message || '').toUpperCase();
        if (code.includes('ID_COLLISION') && i < maxAttempts - 1) continue;
        // Fall back to addDoc if transaction fails for non-collision reasons
        try {
          const fallbackRef = await withBackoff(() => addDoc(Paths.userNotificationsCol(targetUid), { message_id: candidate, ...payload }));
          return fallbackRef.id || candidate;
        } catch (ee) {
          throw ee;
        }
      }
    }
    // Should not reach here
    const id = uuidv4();
    const finalRef = Paths.userNotificationDoc(targetUid, id);
    await withBackoff(() => setDoc(finalRef, { message_id: id, ...payload }, { merge: false }));
    return id;
  }

  private async dispatchToChannels(_targetUid: string, _messageId: string, channels: ChannelName[]): Promise<DeliveryStatus> {
    const status: DeliveryStatus = {};
    if (channels.includes('in_app')) status.in_app = 'SENT';
    return status;
  }
}

export default PlatformMessageService;



